import { useEffect, useLayoutEffect, useState } from "react";

import type { CampaignPayload, GameState } from "../types";

function remaining(value: number, elapsedSeconds: number): number {
  return Math.max(0, value - elapsedSeconds);
}

/**
 * Advances only values that are already presented as live countdowns. This is
 * deliberately not a client simulation: resources, needs, work completion,
 * encounters, revisions, and command inputs remain server-authoritative.
 */
export function advancePresentationState(
  state: GameState,
  elapsedSeconds: number,
): GameState {
  const seconds = Math.max(0, Math.floor(elapsedSeconds));
  if (seconds === 0) return state;

  return {
    ...state,
    game_time: state.game_time + seconds,
    tasks: state.tasks.map((task) => ({
      ...task,
      seconds_left: task.seconds_left === null
        ? null
        : remaining(task.seconds_left, seconds),
    })),
    expeditions: state.expeditions.map((expedition) => ({
      ...expedition,
      contact: expedition.contact
        ? {
          ...expedition.contact,
          return_eta_range_seconds: expedition.contact.return_eta_range_seconds.map(
            (value) => remaining(value, seconds),
          ) as [number, number],
        }
        : undefined,
    })),
    threat: state.threat
      ? {
        ...state.threat,
        eta_range_seconds: state.threat.eta_range_seconds.map(
          (value) => remaining(value, seconds),
        ) as [number, number],
      }
      : null,
  };
}

export function presentationClockCanAdvance(
  payload: CampaignPayload,
  busy: boolean,
): boolean {
  return !busy
    && payload.state.community_status === "holding"
    && !payload.crisis
    && !payload.state.active_crisis;
}

/**
 * Maintains a display-only one-second clock between authoritative payloads.
 * Every new payload replaces the presentation snapshot in full, so polling or
 * a command response always reconciles any local approximation.
 */
export function usePresentationState(
  payload: CampaignPayload,
  busy: boolean,
): GameState {
  const [presentationState, setPresentationState] = useState(payload.state);
  const canAdvance = presentationClockCanAdvance(payload, busy);

  useLayoutEffect(() => {
    setPresentationState(payload.state);
  }, [payload]);

  useEffect(() => {
    if (!canAdvance) return;
    const timer = window.setInterval(() => {
      setPresentationState((current) => advancePresentationState(current, 1));
    }, 1_000);
    return () => window.clearInterval(timer);
  }, [canAdvance, payload]);

  return presentationState;
}
